import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { USER_ROLES } from '../../constants/authConstants';

// Roles permitted by default: Admin, Planning Official and DoNER Official.
// Pass `allowedRoles` to narrow (or widen) access for a specific route.
const DEFAULT_ALLOWED_ROLES = [
  USER_ROLES.ADMIN,
  USER_ROLES.PLANNING_OFFICIAL,
  USER_ROLES.DONER
];

const AdminRoute = ({ children, allowedRoles = DEFAULT_ALLOWED_ROLES }) => {
  const { user, loading, getUserRole } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-secondary flex items-center justify-center">
        <div className="card p-12">
          <div className="flex flex-col items-center space-y-4">
            <svg className="animate-spin w-8 h-8 text-accent" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="text-sm text-text-secondary font-medium">Verifying permissions...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    console.log('AdminRoute: User not authenticated, redirecting to login');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const userRole = getUserRole();
  
  if (!allowedRoles.includes(userRole)) {
    console.log('AdminRoute: User not authorized for admin access');
    return (
      <div className="min-h-screen bg-bg-secondary flex items-center justify-center p-4">
        <div className="card p-12 max-w-md w-full text-center">
          <div className="space-y-6">
            <div className="flex justify-center">
              <div className="w-16 h-16 bg-error-subtle rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-error" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="text-xl font-semibold text-text-primary">Access Denied</h3>
              <p className="text-text-secondary text-sm leading-relaxed">
                You don't have permission to access this page. This area is restricted to authorized roles only.
              </p>
            </div>

            <div className="bg-surface-secondary rounded-lg p-4">
              <div className="text-xs text-text-tertiary space-y-1">
                <p><span className="font-medium">Current User:</span> {user.fullName}</p>
                <p><span className="font-medium">Role:</span> {userRole}</p>
                <p><span className="font-medium">Email:</span> {user.email}</p>
              </div>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => window.history.back()}
                className="btn-primary w-full"
              >
                Go Back
              </button>
              
              <button
                onClick={() => window.location.href = '/dashboard'}
                className="text-sm text-text-tertiary hover:text-text-primary transition-colors"
              >
                Return to Dashboard
              </button>
            </div>

            <div className="border-t border-border-primary pt-4">
              <p className="text-xs text-text-tertiary">
                Need admin access? Contact your system administrator.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

export default AdminRoute;